import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RowDragEvent } from 'ag-grid-community';
import { toast } from 'react-toastify';
import PageHeader from '../../../components/PageHeader';
import BrandFaqsTable from '../../../components/BrandFaqsTable';
import DeleteItemModal from '../../../components/DeleteItemModal';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import useResponsive from '../../../hooks/useResponsive';
import { useBrandContext } from '../../../contexts/BrandContext/BrandContext';
import { deleteBrandFaq, getBrandFaqs, reorderBrandFaqs } from '../../../api/brandFaqs';
import { PAGE_BRAND_FAQS } from '../../../constants/UriConstants';
import './styles/_brand-faqs.scss';
import { BrandFaqInterface } from '../../../types/BrandFaqInterface';

export const FAQS_INTRO =
  "Answer the questions guests ask most. Your FAQs appear on your website's homepage, help guests find what they need, and help your site show up in search results.";

const toastError = (message: string) => toast.error(message, { position: toast.POSITION.TOP_RIGHT });

const BrandFaqsPage = () => {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();
  const { currentBrand } = useBrandContext();
  const brandID = currentBrand?.id;

  const [faqs, setFaqs] = useState<BrandFaqInterface[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<BrandFaqInterface | null>(null);

  useEffect(() => {
    if (!brandID) return undefined;
    let canceled = false;
    setIsLoading(true);
    setLoadFailed(false);
    getBrandFaqs(brandID)
      .then((data) => {
        if (!canceled) setFaqs(data);
      })
      .catch(() => {
        if (canceled) return;
        setLoadFailed(true);
        toastError('Could not load your FAQs. Please try again.');
      })
      .finally(() => {
        if (!canceled) setIsLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [brandID]);

  const handleEdit = useCallback((id: number) => navigate(`/${brandID}/${PAGE_BRAND_FAQS}/${id}`), [brandID, navigate]);

  const handleDelete = useCallback((id: number) => setFaqToDelete(faqs.find((faq) => faq.id === id) ?? null), [faqs]);

  const confirmDelete = async () => {
    if (!brandID || !faqToDelete) return;
    const deleting = faqToDelete;
    setFaqToDelete(null);
    try {
      await deleteBrandFaq(brandID, deleting.id);
      setFaqs((current) => current.filter((faq) => faq.id !== deleting.id));
    } catch {
      toastError('Could not delete the FAQ. Please try again.');
    }
  };

  const handleDragEnd = (event: RowDragEvent) => {
    if (!brandID) return;
    const reordered: BrandFaqInterface[] = [];
    event.api.forEachNode((node) => reordered.push(node.data));
    const previous = faqs;
    setFaqs(reordered);
    reorderBrandFaqs(
      brandID,
      reordered.map((faq) => faq.id)
    ).catch(() => {
      setFaqs(previous);
      toastError('Could not save the new FAQ order. Please try again.');
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="brand-faqs-page__loading">
          <LoadingSpinner size={40} />
        </div>
      );
    }
    if (loadFailed) {
      return <p className="brand-faqs-page__intro">Your FAQs could not be loaded. Refresh the page to try again.</p>;
    }
    if (faqs.length === 0) {
      return <p className="brand-faqs-page__intro">{FAQS_INTRO}</p>;
    }
    return (
      <BrandFaqsTable
        faqs={faqs}
        isDesktop={isDesktop}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDragEnd={handleDragEnd}
      />
    );
  };

  return (
    <div className="brand-faqs-page">
      <PageHeader title="FREQUENTLY ASKED QUESTIONS">
        <button
          type="button"
          className="brand-faqs-page__add-button"
          onClick={() => navigate(`/${brandID}/${PAGE_BRAND_FAQS}/new`)}
        >
          ADD FAQ
        </button>
      </PageHeader>

      {renderContent()}

      <DeleteItemModal
        className="brand-faq-delete-modal"
        isOpen={faqToDelete !== null}
        title="DELETE FAQ"
        message={`Delete "${faqToDelete?.question ?? ''}"? It will be removed from your website.`}
        onCancel={() => setFaqToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default BrandFaqsPage;
